import { normalizeEmail } from "../utils";
import { fetchUserSettings, updateUserSettings } from "./settings";

export const RECENT_INVITEES_CAP = 20;

/**
 * Order a friend list by recency: friends present in the recentInvitees MRU
 * come first (in MRU order), then the remainder alphabetically by display name
 * (falling back to email). Pure; deduped by normalized email.
 */
export function rankFriends(friends, recentInvitees) {
  const list = Array.isArray(friends) ? friends : [];
  const mru = (Array.isArray(recentInvitees) ? recentInvitees : [])
    .map((email) => normalizeEmail(email))
    .filter(Boolean);

  const rankByEmail = new Map();
  mru.forEach((email, index) => {
    if (!rankByEmail.has(email)) rankByEmail.set(email, index);
  });

  const seen = new Set();
  const deduped = [];
  for (const friend of list) {
    const key = normalizeEmail(friend?.email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(friend);
  }

  const inMru = [];
  const rest = [];
  for (const friend of deduped) {
    const key = normalizeEmail(friend.email);
    if (rankByEmail.has(key)) inMru.push(friend);
    else rest.push(friend);
  }

  inMru.sort(
    (a, b) =>
      rankByEmail.get(normalizeEmail(a.email)) -
      rankByEmail.get(normalizeEmail(b.email))
  );
  rest.sort((a, b) => {
    const an = (a.displayName || a.email || "").toLowerCase();
    const bn = (b.displayName || b.email || "").toLowerCase();
    return an.localeCompare(bn);
  });

  return [...inMru, ...rest];
}

/**
 * Best-effort: record the given (successful) invitee emails into the user's
 * recentInvitees MRU. Prepends most-recent-first, dedups by normalized email,
 * caps at RECENT_INVITEES_CAP. Never throws — recency is never allowed to break
 * an invite.
 *
 * Accepted residual: this is a read-modify-write (fetch then merge-set the whole
 * array), so two near-simultaneous invites from the same user can drop one bump
 * (last write wins). Negligible for this app's single-user-per-MRU write volume,
 * and it self-heals on the next invite; not worth a transaction.
 */
export async function recordRecentInvitees(userId, emails) {
  try {
    if (!userId) return;
    const incoming = (emails || [])
      .map((email) => normalizeEmail(email))
      .filter(Boolean);
    if (incoming.length === 0) return;

    const data = await fetchUserSettings(userId);
    const existing = Array.isArray(data?.recentInvitees)
      ? data.recentInvitees
      : [];

    const merged = [];
    const seen = new Set();
    for (const email of [...incoming, ...existing]) {
      const key = normalizeEmail(email);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(key);
      if (merged.length >= RECENT_INVITEES_CAP) break;
    }

    await updateUserSettings(userId, { recentInvitees: merged });
  } catch (err) {
    console.warn("Failed to record recent invitees:", err);
  }
}
